/**
 * Response body display helpers (editor content + language detection).
 */

import { canFormatCode, formatCode, formatJson } from './formatCode';
import { redactResponse } from './redactResponse';

/**
 * The single gate for exposing a response body to the AI model (P3 privacy).
 * When the user has NOT enabled "Let AI read responses" the body is withheld and
 * a machine-readable block marker is returned instead. When enabled, the response
 * is redacted (headers allowlisted, secrets scrubbed, body truncated) first.
 *
 * @param {object} response { status, statusText, headers, data }
 * @param {{ allowed: boolean, maxChars?: number }} opts
 */
export function getResponseBodyForAi(response, { allowed, maxChars } = {}) {
  if (!response) {
    return { blocked: false, error: 'No response captured yet. Send the request first.' };
  }
  if (!allowed) {
    return {
      blocked: true,
      reason:
        'The user has not enabled "Let AI read responses". Ask them to turn on that toggle in the AI panel header if you need to read the body.',
      // Non-sensitive metadata is always safe to share so the model has context.
      status: response.status ?? null,
      status_text: response.statusText ?? response.status_text ?? '',
    };
  }
  return { blocked: false, ...redactResponse(response, { maxChars }) };
}


/**
 * The body exactly as received, for test scripts, history and examples.
 * Display formatting belongs to the viewer (getResponseBodyEditorContent):
 * a formatted copy handed to `pm.response` rounded integers above 2^53.
 */
export function getResponseRawText(response) {
  const data = response?.data;
  if (typeof data === 'string') return data;
  if (data == null) return '';
  return JSON.stringify(data, null, 2);
}

/** True when the response carried bytes that `data` cannot represent. */
export function isBinaryResponse(response) {
  return typeof response?.data_base64 === 'string' && response.data_base64.length > 0;
}

/** A `data:` URL for a binary body, or '' when there is none. */
export function binaryDataUrl(response) {
  if (!isBinaryResponse(response)) return '';
  const mime = (response.headers?.['content-type'] || 'application/octet-stream').split(';')[0].trim();
  return `data:${mime};base64,${response.data_base64}`;
}

function binaryPlaceholder(response) {
  const mime = (response.headers?.['content-type'] || 'application/octet-stream').split(';')[0].trim();
  const bytes = Number(response.size) || 0;
  return `[${mime} — ${bytes.toLocaleString()} bytes of binary data. Use Preview to view it.]`;
}


/**
 * Classic hex dump of a binary body: offset, 16 bytes, ASCII gutter.
 *
 * Only reachable for a body that arrived as `data_base64` — `response.data`
 * for a binary is from_utf8_lossy output, so dumping THAT would show the
 * corruption's bytes rather than the file's, which is worse than showing
 * nothing.
 *
 * Capped: a hex dump is ~4x the size of the bytes and Monaco is not a viewer
 * for tens of megabytes of it. The cut is announced in the output rather than
 * silent — a truncated dump that says nothing is indistinguishable from a
 * short file.
 */
const HEX_MAX_BYTES = 512 * 1024;

export function responseHexDump(response) {
  if (!isBinaryResponse(response)) return '';
  let bytes;
  try {
    const bin = atob(response.data_base64);
    bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  } catch {
    return '';
  }

  const total = bytes.length;
  const shown = Math.min(total, HEX_MAX_BYTES);
  const lines = [];
  for (let off = 0; off < shown; off += 16) {
    const row = bytes.subarray(off, Math.min(off + 16, shown));
    const hex = [];
    for (let i = 0; i < 16; i += 1) {
      hex.push(i < row.length ? row[i].toString(16).padStart(2, '0') : '  ');
      // The 8-byte gutter every hex viewer has — it is what lets you count to
      // a byte offset by eye instead of by finger.
      if (i === 7) hex.push('');
    }
    const ascii = Array.from(row)
      .map((b) => (b >= 0x20 && b <= 0x7e ? String.fromCharCode(b) : '.'))
      .join('');
    // `00000000:` with a colon — it reads as a LABEL for the row rather than
    // as another hex value, which matters now that real bytes sit beside it.
    // Wide gap before the ASCII gutter so the two columns do not run together;
    // the row is padded to a fixed width so that gap lands in the same place
    // on a short final row.
    lines.push(`${off.toString(16).padStart(8, '0')}:  ${hex.join(' ').padEnd(50, ' ')}    |${ascii}|`);
  }
  if (shown < total) {
    lines.push('');
    lines.push(`[--- truncated: showing first ${shown.toLocaleString()} of ${total.toLocaleString()} bytes ---]`);
  }
  return lines.join('\n');
}

export function getResponseContentType(response) {
  const contentType = response.headers?.['content-type'] || '';
  const lowerContentType = contentType.toLowerCase();

  if (lowerContentType.includes('application/json') || lowerContentType.includes('text/json')) {
    return 'json';
  }
  if (
    lowerContentType.includes('application/xml') ||
    lowerContentType.includes('text/xml') ||
    lowerContentType.includes('application/soap+xml') ||
    lowerContentType.includes('text/soap+xml')
  ) {
    return 'xml';
  }
  if (lowerContentType.includes('text/html')) return 'html';
  if (lowerContentType.includes('text/css')) return 'css';
  if (
    lowerContentType.includes('application/javascript') ||
    lowerContentType.includes('text/javascript')
  ) {
    return 'javascript';
  }
  if (
    lowerContentType.includes('application/yaml') ||
    lowerContentType.includes('text/yaml') ||
    lowerContentType.includes('application/x-yaml')
  ) {
    return 'yaml';
  }
  // Binary families the viewer can PREVIEW rather than print as mojibake.
  // Returned before the text fallbacks so `application/pdf` never lands on
  // 'text' and gets handed to the code editor.
  if (lowerContentType.includes('application/pdf')) return 'pdf';
  if (lowerContentType.startsWith('image/')) return 'image';

  if (lowerContentType.includes('text/plain')) return 'text';
  if (lowerContentType.includes('text/csv')) return 'csv';
  if (lowerContentType.includes('text/')) return 'text';

  return 'text';
}

/**
 * Raw body text + syntax language (before beautify).
 * @returns {{ text: string, language: string }}
 */
export function getResponseBodyRaw(response) {
  const contentType = getResponseContentType(response);

  // A binary body has no honest text form. `response.data` for one of these is
  // `from_utf8_lossy` output — U+FFFD noise that looks like corruption because
  // it IS corruption. Say so instead of printing it.
  if (isBinaryResponse(response)) {
    return { language: 'text', text: binaryPlaceholder(response) };
  }

  if (typeof response.data === 'object' && response.data !== null) {
    return {
      language: 'json',
      text: JSON.stringify(response.data, null, 2),
    };
  }

  const raw = String(response.data ?? '');

  switch (contentType) {
    case 'json':
      return { language: 'json', text: raw };
    case 'html':
      return { language: 'html', text: raw };
    case 'xml':
      return { language: 'xml', text: raw };
    case 'css':
      return { language: 'css', text: raw };
    case 'javascript':
      return { language: 'javascript', text: raw };
    case 'yaml':
      return { language: 'yaml', text: raw };
    case 'csv':
      return { language: 'csv', text: raw };
    default:
      return { language: 'text', text: raw };
  }
}

function tryDetectJsonLanguage(text, language) {
  if (language !== 'text' || !text.trim()) return language;
  try {
    JSON.parse(text);
    return 'json';
  } catch {
    return language;
  }
}

function beautifyBodyText(text, language) {
  let lang = tryDetectJsonLanguage(text, language);
  if (!canFormatCode(lang)) {
    if (lang === 'json') {
      try {
        return { language: 'json', text: formatJson(text) };
      } catch {
        return { language: lang, text };
      }
    }
    return { language: lang, text };
  }
  const result = formatCode(lang, text);
  if (result.ok) {
    return { language: lang, text: result.value };
  }
  return { language: lang, text };
}

/**
 * Display text for the response body editor (auto-beautified when possible).
 * @returns {{ text: string, language: string }}
 */
export function getResponseBodyEditorContent(response) {
  const raw = getResponseBodyRaw(response);
  return beautifyBodyText(raw.text, raw.language);
}
